import React, { useMemo, useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faTrash,
  faWarehouse,
  faXmark,
} from '@fortawesome/free-solid-svg-icons';

/**
 * One delivery stop. Shows only the articles that were explicitly added to
 * this destination (a stop usually gets a handful of the 13 articles), each
 * with available stock and per-article contingent feedback.
 *
 * `line` shape: { destination, destinationName, destinationType, articles: [{ stock, quantity }] }
 * `contingentLookup` is keyed `${destinationId}|${articleId}` (raw UUIDs).
 */
const DeliveryDestinationCard = ({
  line,
  stocks = [],
  contingentLookup = {},
  onAddArticle,
  onRemoveArticle,
  onChangeQuantity,
  onRemove,
}) => {
  // Track the most recently added article so we can focus its input.
  const [justAddedStockId, setJustAddedStockId] = useState(null);

  const usedStockIds = useMemo(
    () => new Set(line.articles.map((a) => a.stock.id)),
    [line.articles]
  );

  const availableStocks = useMemo(
    () => stocks.filter((s) => !usedStockIds.has(s.id)),
    [stocks, usedStockIds]
  );

  const totalUnits = line.articles.reduce(
    (sum, a) => sum + (a.quantity || 0),
    0
  );

  const handleAddArticle = (e) => {
    const stockId = e.target.value;
    if (!stockId) return;
    const stock = stocks.find((s) => s.id === stockId);
    if (!stock) return;
    setJustAddedStockId(stockId);
    onAddArticle(line.destination, stock);
    e.target.value = '';
  };

  return (
    <div className='card mb-3'>
      <div className='card-header d-flex justify-content-between align-items-center'>
        <div>
          <span className='fw-bold'>{line.destinationName}</span>
          <span
            className={`badge ms-2 ${
              line.destinationType === 'backstage'
                ? 'bg-info'
                : 'bg-secondary'
            }`}
          >
            {line.destinationType === 'backstage' ? 'Backstage' : 'Crew'}
          </span>
        </div>
        <button
          type='button'
          className='btn btn-sm btn-outline-danger'
          title='Lieferstelle entfernen'
          onClick={() => onRemove(line.destination)}
        >
          <FontAwesomeIcon icon={faTrash} />
        </button>
      </div>

      <div className='card-body'>
        {line.articles.length === 0 ? (
          <p className='text-muted mb-3'>
            Noch keine Artikel. Unten Artikel hinzufügen.
          </p>
        ) : (
          <table className='table table-sm align-middle mb-3'>
            <tbody>
              {line.articles.map((article) => {
                const status =
                  contingentLookup[
                    `${line.destination}|${article.stock.article}`
                  ];
                const remaining = status
                  ? status.remaining - article.quantity
                  : null;
                const overflow = remaining !== null && remaining < 0;
                const overStock =
                  article.quantity > article.stock.quantity_available;
                return (
                  <tr key={article.stock.id}>
                    <td>{article.stock.article_name}</td>
                    <td
                      className={`text-nowrap small ${
                        overStock ? 'text-danger fw-bold' : 'text-muted'
                      }`}
                    >
                      {article.stock.quantity_available}
                      <FontAwesomeIcon
                        icon={faWarehouse}
                        className='ms-1'
                        title='verfügbarer Bestand'
                      />
                    </td>
                    <td style={{ width: '7rem' }}>
                      <input
                        className={`form-control form-control-sm ${
                          overflow || overStock ? 'border-danger' : ''
                        }`}
                        type='number'
                        min='0'
                        autoFocus={article.stock.id === justAddedStockId}
                        value={article.quantity}
                        onChange={(e) =>
                          onChangeQuantity(
                            line.destination,
                            article.stock.id,
                            parseInt(e.target.value, 10) || 0
                          )
                        }
                      />
                    </td>
                    <td className='small text-nowrap'>
                      {status && (
                        <span
                          className={
                            overflow ? 'text-danger fw-bold' : 'text-muted'
                          }
                        >
                          übrig: {remaining}/{status.contingent}
                        </span>
                      )}
                    </td>
                    <td className='text-end' style={{ width: '2.5rem' }}>
                      <button
                        type='button'
                        className='btn btn-sm btn-link text-secondary p-0'
                        title='Artikel entfernen'
                        onClick={() =>
                          onRemoveArticle(line.destination, article.stock.id)
                        }
                      >
                        <FontAwesomeIcon icon={faXmark} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}

        <select
          className='form-select form-select-sm'
          value=''
          disabled={availableStocks.length === 0}
          onChange={handleAddArticle}
        >
          <option value='' disabled>
            {availableStocks.length === 0
              ? 'Alle Artikel hinzugefügt'
              : '+ Artikel hinzufügen…'}
          </option>
          {availableStocks.map((stock) => (
            <option key={stock.id} value={stock.id}>
              {stock.article_name}
            </option>
          ))}
        </select>
      </div>

      <div className='card-footer text-muted small'>
        {line.articles.length} Positionen · {totalUnits} Einheiten
      </div>
    </div>
  );
};

export default DeliveryDestinationCard;
